// Global Instructions Rule Applied!
import React from 'react'
import { Checkbox } from 'antd'
import { LabelDateBadge, UserIconList } from '../../../core/components'
import { TaskRecord, TaskRecordFields } from '../../../state_manager/data_models/task_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import { sortTasksCanonical } from '../utils/taskSortUtils.ts'
import { taskDisplaySubject } from '../utils/mergeTaskUpdate.ts'

/** One styled slot of the mini list; `style` and `completedClassName` are only set where a slot needs them. */
type StyleSlot = { className: string; style?: React.CSSProperties; completedClassName?: string }
type StyleSlotKey = 'container' | 'taskRow' | 'checkbox' | 'contentContainer' | 'taskText' | 'assigneeContainer' | 'dateContainer'

interface TaskListMiniProps {
  tasks?: TaskRecord[]
  maxVisible?: number
  maxTextLength?: number
  showCompleted?: boolean
  onTaskClick?: (task: TaskRecord, allTasks: TaskRecord[], record?: any) => void
  onTaskToggle?: (taskId: string, recordId: string, completed: boolean) => void
  record?: any
}

/**
 * Reusable mini task list component for displaying tasks in compact format
 */
const TaskListMini: React.FC<TaskListMiniProps> = ({
  tasks = [],
  maxVisible = 3,
  maxTextLength = 40,
  showCompleted = false,
  onTaskClick,
  onTaskToggle,
  record
}) => {
  // Ensure tasks is an array
  const taskArray = Array.isArray(tasks) ? tasks : []

  // Canonical sort: open first (sort_order or created_at), completed second (completed_on DESC)
  const sortedTasks = sortTasksCanonical([...taskArray])

  // Filter tasks based on completion status
  const filteredTasks = showCompleted
    ? sortedTasks
    : sortedTasks.filter((task) => !task[TaskRecordFields.tasks__completed])

  const taskCount = filteredTasks.length

  // Return empty if no tasks to display
  if (taskCount === 0) {
    return <span></span>
  }

  const displayTasks = filteredTasks.slice(0, maxVisible)

  // Default styling with override capability
  const defaultStyling: Record<StyleSlotKey, StyleSlot> = {
    container: {
      className: 'space-y-1'
    },
    taskRow: {
      className: 'flex items-center gap-1 text-xs text-gray-700 leading-none h-4'
    },
    checkbox: {
      className: 'scale-75'
    },
    contentContainer: {
      className: 'flex items-center flex-1 min-w-0 gap-2'
    },
    taskText: {
      className: 'truncate cursor-pointer hover:text-blue-600 hover:underline flex-1',
      completedClassName: 'line-through text-gray-400'
    },
    assigneeContainer: {
      className: 'w-9 flex justify-end flex-shrink-0'
    },
    dateContainer: {
      className: 'w-7 flex justify-end flex-shrink-0'
    }
  }

  return (
    <div className={defaultStyling.container.className} style={defaultStyling.container.style}>
      {displayTasks.map((task, taskIndex) => {
        // Truncate task subject to specified length
        const taskSubject = taskDisplaySubject(task)
        const truncatedSubject =
          taskSubject.length > maxTextLength ? `${taskSubject.substring(0, maxTextLength)}...` : taskSubject

        const taskId = task[TaskRecordFields.tasks__id]
        const taskCompleted = task[TaskRecordFields.tasks__completed] || false
        const taskDueBy = task[TaskRecordFields.tasks__due_by]
        const assignees = task[TaskRecordFields.prog__task_assignees] || []

        return (
          <div
            key={taskId || taskIndex}
            className={defaultStyling.taskRow.className}
            style={defaultStyling.taskRow.style}
            title={taskSubject}
          >
            <Checkbox
              className={defaultStyling.checkbox.className}
              style={defaultStyling.checkbox.style}
              checked={taskCompleted}
              onChange={(e) => {
                e.stopPropagation()
                if (onTaskToggle && taskId) {
                  // Extract record ID - check id first (normalized in tables), then specific field names
                  const recordId =
                    record?.id || record?.deals__id || record?.prospects__id || record?.accounts__id || ''
                  onTaskToggle(taskId, recordId, !taskCompleted)
                }
              }}
            />
            <div className={defaultStyling.contentContainer.className} style={defaultStyling.contentContainer.style}>
              <div
                className={`${defaultStyling.taskText.className} ${
                  taskCompleted ? defaultStyling.taskText.completedClassName : ''
                }`}
                style={defaultStyling.taskText.style}
                onClick={(e) => {
                  e.stopPropagation()
                  if (onTaskClick) {
                    onTaskClick(task, taskArray, record)
                  }
                }}
              >
                {truncatedSubject}
              </div>
              {/* Assignee initials */}
              <div
                className={defaultStyling.assigneeContainer.className}
                style={defaultStyling.assigneeContainer.style}
              >
                {assignees.length > 0 && (
                  <UserIconList
                    contactNames={assignees.map(
                      (assignee: any) =>
                        assignee[UserEntryFields.prog__display_name] ||
                        `${assignee[UserEntryFields.contacts__first_name] || ''} ${assignee[UserEntryFields.contacts__last_name] || ''}`.trim()
                    )}
                    contactIds={assignees.map((assignee: any) => assignee[UserEntryFields.contacts__id] || '')}
                    maxVisible={2}
                    size='small'
                  />
                )}
              </div>
              {/* Due date */}
              <div className={defaultStyling.dateContainer.className} style={defaultStyling.dateContainer.style}>
                {taskDueBy && <LabelDateBadge date={taskDueBy} size='compact' textSize='10px' />}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default TaskListMini
